import React, { useState, useEffect } from 'react';
import { 
  Plus, X, Upload, Image as ImageIcon, Lock, Shield, Search, Filter, 
  Edit2, Trash2, Eye, Package, TrendingUp, Users, AlertCircle, 
  CheckCircle, Clock, DollarSign, BarChart3, Settings, LogOut,
  ChevronDown, Download, RefreshCw, Grid, List, PieChart, BarChart2
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

const ProductAdminEnhanced = () => {
  const { user, isAuthenticated } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('name');
  const [showStats, setShowStats] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSmallMobile, setIsSmallMobile] = useState(false);
  const [showDataViz, setShowDataViz] = useState(false);
  const [chartType, setChartType] = useState('category'); // category, stock, price
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalValue: 0,
    lowStock: 0,
    popularProducts: 0
  });

  // Détecter si on est sur mobile
  useEffect(() => {
    const checkMobile = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsSmallMobile(width < 480);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Vérifier les permissions de l'utilisateur
  useEffect(() => {
    const checkPermissions = async () => {
      if (!isAuthenticated || !user) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch('https://inza-backend.sajholding.org/api/v1/user/profile/', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setUserRole(data.role);
        } else {
          setUserRole('customer');
        }
      } catch (error) {
        console.error('Erreur lors de la vérification des permissions:', error);
        setUserRole('customer');
      } finally {
        setLoading(false);
      }
    };

    checkPermissions();
  }, [isAuthenticated, user]);

  // Charger les produits
  const loadProducts = async () => {
    try {
      const response = await fetch('https://inza-backend.sajholding.org/api/v1/products/', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProducts(data);
        calculateStats(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
    }
  };

  // Calculer les statistiques
  const calculateStats = (productsData) => {
    const stats = {
      totalProducts: productsData.length,
      totalValue: productsData.reduce((sum, p) => sum + (p.price * p.stock), 0),
      lowStock: productsData.filter(p => p.stock < 5).length,
      popularProducts: productsData.filter(p => p.popular).length
    };
    setStats(stats);
  };

  // Calculer les données pour les graphiques
  const getCategoryData = () => {
    const categoryCount = {};
    products.forEach(product => {
      categoryCount[product.category] = (categoryCount[product.category] || 0) + 1;
    });
    return Object.entries(categoryCount).map(([category, count]) => ({
      name: category.charAt(0).toUpperCase() + category.slice(1),
      value: count,
      percentage: Math.round((count / products.length) * 100)
    }));
  };

  const getStockData = () => {
    return products.map(product => ({
      name: product.name.length > 20 ? product.name.substring(0, 20) + '...' : product.name,
      stock: product.stock,
      category: product.category
    })).sort((a, b) => b.stock - a.stock).slice(0, 10);
  };

  const getPriceData = () => {
    return products.map(product => ({
      name: product.name.length > 20 ? product.name.substring(0, 20) + '...' : product.name,
      price: product.price,
      category: product.category
    })).sort((a, b) => b.price - a.price).slice(0, 10);
  };

  // Vérifier si l'utilisateur peut gérer les produits
  const canManageProducts = userRole && ['manager', 'admin', 'super_admin'].includes(userRole);

  // Charger les produits au démarrage
  useEffect(() => {
    if (canManageProducts) {
      loadProducts();
    }
  }, [canManageProducts]);

  // Filtrer et trier les produits
  const filteredAndSortedProducts = products
    .filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = filterCategory === 'all' || product.category === filterCategory;
      const matchesStatus = filterStatus === 'all' || 
        (filterStatus === 'low-stock' && product.stock < 5) ||
        (filterStatus === 'popular' && product.popular) ||
        (filterStatus === 'out-of-stock' && product.stock === 0);
      return matchesSearch && matchesCategory && matchesStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'price':
          return a.price - b.price;
        case 'stock':
          return b.stock - a.stock;
        case 'created_at':
          return new Date(b.created_at) - new Date(a.created_at);
        default:
          return 0;
      }
    });

  // Styles CSS
  const adminStyles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      fontFamily: 'Arial, sans-serif',
      overflowX: 'hidden'
    },
    header: {
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      paddingTop: '1rem',
      paddingBottom: '1rem',
      paddingLeft: '2rem',
      paddingRight: '2rem',
      borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      overflowX: 'hidden'
    },
    headerContent: {
      maxWidth: '1400px',
      marginLeft: 'auto',
      marginRight: 'auto',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      overflowX: 'hidden'
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: '#4f46e5'
    },
    userInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      flexShrink: 0,
      minWidth: 0
    },
    statsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1.5rem',
      paddingTop: '2rem',
      paddingBottom: '2rem',
      paddingLeft: '2rem',
      paddingRight: '2rem',
      maxWidth: '1400px',
      marginLeft: 'auto',
      marginRight: 'auto',
      overflowX: 'hidden'
    },
    statCard: {
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      padding: '1.5rem',
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)'
    },
    controlsContainer: {
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      padding: '1.5rem',
      marginTop: '0',
      marginLeft: 'auto',
      marginRight: 'auto',
      marginBottom: '2rem',
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      maxWidth: '1360px'
    },
    searchAndFilters: {
      display: 'flex',
      gap: '1rem',
      flexWrap: 'wrap',
      alignItems: 'center',
      marginBottom: '1rem',
      overflowX: 'hidden',
      minWidth: 0
    },
    searchInput: {
      flex: 1,
      minWidth: '150px',
      padding: '0.75rem',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '1rem',
      maxWidth: '100%'
    },
    filterSelect: {
      padding: '0.75rem',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '1rem',
      background: 'white',
      minWidth: '120px',
      maxWidth: '100%'
    },
    button: {
      padding: '0.75rem 1.5rem',
      border: 'none',
      borderRadius: '8px',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    primaryButton: {
      background: '#4f46e5',
      color: 'white'
    },
    secondaryButton: {
      background: '#f3f4f6',
      color: '#374151'
    },
    productsContainer: {
      paddingTop: '0',
      paddingBottom: '2rem',
      paddingLeft: '2rem',
      paddingRight: '2rem',
      maxWidth: '1400px',
      marginLeft: 'auto',
      marginRight: 'auto',
      overflowX: 'hidden'
    },
    productsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '1.5rem'
    },
    productsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem'
    },
    productCardList: {
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      borderRadius: '12px',
      padding: '1rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      transition: 'transform 0.3s ease'
    },
    productListImage: {
      width: '80px',
      height: '80px',
      objectFit: 'cover',
      borderRadius: '8px',
      flexShrink: 0
    },
    productListInfo: {
      flex: 1,
      minWidth: 0
    },
    productListActions: {
      display: 'flex',
      flexDirection: 'row',
      gap: '0.25rem',
      flexShrink: 0
    },
    productCard: {
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      borderRadius: '12px',
      padding: '1.5rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      transition: 'transform 0.3s ease'
    },
    productImage: {
      width: '100%',
      height: '200px',
      objectFit: 'cover',
      borderRadius: '8px',
      marginBottom: '1rem'
    },
    productActions: {
      display: 'flex',
      gap: '0.5rem',
      marginTop: '1rem'
    },
    actionButton: {
      padding: '0.5rem',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    actionButtonSmall: {
      padding: '0.25rem',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    // Styles mobiles
    mobileHeader: {
      paddingTop: '1rem',
      paddingBottom: '1rem',
      paddingLeft: '1rem',
      paddingRight: '1rem',
      flexDirection: 'column',
      gap: '1rem'
    },
    mobileLogo: {
      fontSize: '1.2rem'
    },
    mobileStatsContainer: {
      gridTemplateColumns: '1fr',
      gap: '1rem',
      paddingLeft: '1rem',
      paddingRight: '1rem'
    },
    mobileControlsContainer: {
      marginTop: '0',
      marginLeft: '1rem',
      marginRight: '1rem',
      marginBottom: '1rem',
      padding: '1rem'
    },
    mobileSearchAndFilters: {
      flexDirection: 'column',
      gap: '0.75rem'
    },
    mobileProductsContainer: {
      paddingLeft: '1rem',
      paddingRight: '1rem',
      paddingBottom: '1rem'
    },
    mobileProductsGrid: {
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '1rem'
    },
    mobileProductCardList: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '0.75rem',
      padding: '0.75rem'
    },
    mobileProductListImage: {
      width: '60px',
      height: '60px'
    },
    mobileProductListInfo: {
      width: '100%'
    },
    mobileProductListActions: {
      width: '100%',
      justifyContent: 'flex-end'
    },
    smallMobileProductsGrid: {
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '0.75rem'
    },
    // Styles pour les graphiques
    dataVizContainer: {
      background: 'rgba(255, 255, 255, 0.98)',
      backdropFilter: 'blur(20px)',
      padding: '2.5rem',
      margin: '2rem 2rem 2rem',
      borderRadius: '16px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      maxWidth: '1400px',
      marginLeft: 'auto',
      marginRight: 'auto',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      position: 'relative',
      overflow: 'hidden'
    },
    chartHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2.5rem',
      paddingBottom: '1rem',
      borderBottom: '2px solid #f3f4f6'
    },
    chartTabs: {
      display: 'flex',
      gap: '0.25rem',
      background: '#f8fafc',
      padding: '0.25rem',
      borderRadius: '12px',
      border: '1px solid #e2e8f0'
    },
    chartTab: {
      padding: '0.75rem 1.25rem',
      border: 'none',
      borderRadius: '8px',
      background: 'transparent',
      cursor: 'pointer',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#64748b',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    chartTabActive: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      transform: 'scale(1.02)'
    },
    chartContainer: {
      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      padding: '2.5rem',
      borderRadius: '16px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      position: 'relative',
      overflow: 'hidden'
    },
    pieChartContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-around',
      flexWrap: 'wrap',
      gap: '2rem'
    },
    pieChart: {
      width: '250px',
      height: '250px',
      position: 'relative',
      filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))'
    },
    chartLegend: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem'
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.75rem',
      borderRadius: '8px',
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    },
    legendColor: {
      width: '20px',
      height: '20px',
      borderRadius: '6px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
    },
    barChartContainer: {
      width: '100%'
    },
    barChart: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem'
    },
    barItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '1.5rem',
      padding: '0.75rem',
      borderRadius: '8px',
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      transition: 'all 0.3s ease'
    },
    barLabel: {
      minWidth: '150px',
      fontSize: '0.875rem',
      color: '#374151'
    },
    barWrapper: {
      flex: 1,
      height: '32px',
      background: 'linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 100%)',
      borderRadius: '8px',
      overflow: 'hidden',
      position: 'relative',
      boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.06)'
    },
    barFill: {
      height: '100%',
      borderRadius: '8px',
      transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
    },
    barValue: {
      minWidth: '60px',
      textAlign: 'right',
      fontSize: '0.875rem',
      fontWeight: 'bold',
      color: '#1f2937'
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Vérification des permissions...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <Lock size={48} style={{ color: '#6b7280', marginBottom: '20px' }} />
        <h2>Accès restreint</h2>
        <p>Vous devez être connecté pour accéder à l'administration des produits.</p>
        <button 
          onClick={() => window.location.href = '/login'}
          style={{
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: 8,
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
          Se connecter
        </button>
      </div>
    );
  }

  if (!canManageProducts) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <Shield size={48} style={{ color: '#ef4444', marginBottom: '20px' }} />
        <h2>Permissions insuffisantes</h2>
        <p>Vous n'avez pas les permissions nécessaires pour gérer les produits.</p>
        <p style={{ color: '#6b7280', fontSize: '14px' }}>
          Rôle actuel: {userRole === 'customer' ? 'Client' : userRole}
        </p>
        <p style={{ color: '#6b7280', fontSize: '14px' }}>
          Rôles autorisés: Manager, Administrateur, Super Administrateur
        </p>
      </div>
    );
  }

  return (
    <div style={adminStyles.container}>
      {/* Header Admin */}
      <header style={{
        ...adminStyles.header,
        ...(isMobile && adminStyles.mobileHeader)
      }}>
        <div style={{
          ...adminStyles.headerContent,
          ...(isMobile && { flexDirection: 'column', alignItems: 'flex-start' })
        }}>
          <div style={{
            ...adminStyles.logo,
            ...(isMobile && adminStyles.mobileLogo)
          }}>
            <Package size={isMobile ? 24 : 32} />
            <span>INZA Admin</span>
          </div>
          
          <div style={{
            ...adminStyles.userInfo,
            ...(isMobile && { flexDirection: 'column', alignItems: 'flex-start', gap: '0.5rem', width: '100%' })
          }}>
            <span style={{ color: '#6b7280', fontSize: isMobile ? '0.875rem' : '1rem' }}>
              {isMobile ? 'Connecté' : `Connecté: ${user?.email || user?.username}`}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
              <span style={{ 
                background: '#4f46e5', 
                color: 'white', 
                padding: '0.25rem 0.75rem', 
                borderRadius: '9999px',
                fontSize: '0.875rem'
              }}>
                {userRole === 'super_admin' ? 'Super Admin' : 
                 userRole === 'admin' ? 'Admin' : 
                 userRole === 'manager' ? 'Manager' : userRole}
              </span>
              <button 
                style={{ ...adminStyles.actionButton, background: '#ef4444', color: 'white' }}
                onClick={() => {
                  localStorage.removeItem('token');
                  window.location.href = '/login';
                }}
              >
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Tableau de bord avec statistiques */}
      {showStats && (
        <div style={{
          ...adminStyles.statsContainer,
          ...(isMobile && adminStyles.mobileStatsContainer)
        }}>
          <div style={adminStyles.statCard}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <div style={{ background: '#dbeafe', padding: '0.75rem', borderRadius: '8px' }}>
                <Package size={24} style={{ color: '#3b82f6' }} />
              </div>
              <div>
                <h3 style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>Total Produits</h3>
                <p style={{ margin: 0, fontSize: '2rem', fontWeight: 'bold', color: '#1f2937' }}>
                  {stats.totalProducts}
                </p>
              </div>
            </div>
          </div>

          <div style={adminStyles.statCard}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <div style={{ background: '#d1fae5', padding: '0.75rem', borderRadius: '8px' }}>
                <DollarSign size={24} style={{ color: '#10b981' }} />
              </div>
              <div>
                <h3 style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>Valeur Totale</h3>
                <p style={{ margin: 0, fontSize: '2rem', fontWeight: 'bold', color: '#1f2937' }}>
                  {stats.totalValue.toLocaleString()} FCFA
                </p>
              </div>
            </div>
          </div>

          <div style={adminStyles.statCard}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <div style={{ background: '#fee2e2', padding: '0.75rem', borderRadius: '8px' }}>
                <AlertCircle size={24} style={{ color: '#ef4444' }} />
              </div>
              <div>
                <h3 style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>Stock Faible</h3>
                <p style={{ margin: 0, fontSize: '2rem', fontWeight: 'bold', color: '#1f2937' }}>
                  {stats.lowStock}
                </p>
              </div>
            </div>
          </div>

          <div style={adminStyles.statCard}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <div style={{ background: '#fef3c7', padding: '0.75rem', borderRadius: '8px' }}>
                <TrendingUp size={24} style={{ color: '#f59e0b' }} />
              </div>
              <div>
                <h3 style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>Produits Populaires</h3>
                <p style={{ margin: 0, fontSize: '2rem', fontWeight: 'bold', color: '#1f2937' }}>
                  {stats.popularProducts}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Visualisation de données */}
      {showDataViz && (
        <div style={adminStyles.dataVizContainer}>
          <div style={adminStyles.chartHeader}>
            <h2 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem' }}>
              <BarChart2 size={24} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
              Analyse des données
            </h2>
            <div style={adminStyles.chartTabs}>
              <button
                style={{
                  ...adminStyles.chartTab,
                  ...(chartType === 'category' && adminStyles.chartTabActive)
                }}
                onClick={() => setChartType('category')}
              >
                <PieChart size={16} style={{ marginRight: '0.25rem' }} />
                Catégories
              </button>
              <button
                style={{
                  ...adminStyles.chartTab,
                  ...(chartType === 'stock' && adminStyles.chartTabActive)
                }}
                onClick={() => setChartType('stock')}
              >
                <Package size={16} style={{ marginRight: '0.25rem' }} />
                Stock
              </button>
              <button
                style={{
                  ...adminStyles.chartTab,
                  ...(chartType === 'price' && adminStyles.chartTabActive)
                }}
                onClick={() => setChartType('price')}
              >
                <DollarSign size={16} style={{ marginRight: '0.25rem' }} />
                Prix
              </button>
            </div>
          </div>

          <div style={adminStyles.chartContainer}>
            {chartType === 'category' && (
              <div style={adminStyles.pieChartContainer}>
                <div style={adminStyles.pieChart}>
                  <svg width="250" height="250" viewBox="0 0 250 250">
                    <defs>
                      <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
                        <feOffset dx="0" dy="2" result="offsetblur"/>
                        <feFlood flood-color="#000000" flood-opacity="0.2"/>
                        <feComposite in2="offsetblur" operator="in"/>
                        <feMerge>
                          <feMergeNode/>
                          <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                      </filter>
                    </defs>
                    {getCategoryData().map((item, index) => {
                      const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
                      const startAngle = index === 0 ? 0 : getCategoryData().slice(0, index).reduce((sum, d) => sum + d.percentage, 0) * 3.6;
                      const endAngle = startAngle + (item.percentage * 3.6);
                      const x1 = 125 + 100 * Math.cos((startAngle - 90) * Math.PI / 180);
                      const y1 = 125 + 100 * Math.sin((startAngle - 90) * Math.PI / 180);
                      const x2 = 125 + 100 * Math.cos((endAngle - 90) * Math.PI / 180);
                      const y2 = 125 + 100 * Math.sin((endAngle - 90) * Math.PI / 180);
                      const largeArc = item.percentage > 50 ? 1 : 0;
                      
                      return (
                        <g key={item.name}>
                          <path
                            d={`M 125 125 L ${x1} ${y1} A 100 100 0 ${largeArc} 1 ${x2} ${y2} Z`}
                            fill={colors[index % colors.length]}
                            stroke="white"
                            strokeWidth="3"
                            filter="url(#shadow)"
                            style={{
                              transition: 'all 0.3s ease',
                              cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'scale(1.05)';
                              e.currentTarget.style.transformOrigin = '125px 125px';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          />
                          <text
                            x={125 + 70 * Math.cos(((startAngle + endAngle) / 2 - 90) * Math.PI / 180)}
                            y={125 + 70 * Math.sin(((startAngle + endAngle) / 2 - 90) * Math.PI / 180)}
                            fill="white"
                            fontSize="14"
                            fontWeight="bold"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            style={{ pointerEvents: 'none' }}
                          >
                            {item.percentage > 5 ? `${item.percentage}%` : ''}
                          </text>
                        </g>
                      );
                    })}
                  </svg>
                </div>
                <div style={adminStyles.chartLegend}>
                  {getCategoryData().map((item, index) => {
                    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
                    return (
                      <div key={item.name} style={adminStyles.legendItem}>
                        <div style={{ ...adminStyles.legendColor, background: colors[index % colors.length] }} />
                        <span style={{ fontSize: '0.875rem', color: '#374151' }}>
                          {item.name}: {item.value} ({item.percentage}%)
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {chartType === 'stock' && (
              <div style={adminStyles.barChartContainer}>
                <h3 style={{ margin: '0 0 1.5rem 0', color: '#1f2937' }}>Top 10 produits par stock</h3>
                <div style={adminStyles.barChart}>
                  {getStockData().map((item, index) => {
                    const maxStock = Math.max(...getStockData().map(d => d.stock));
                    const percentage = (item.stock / maxStock) * 100;
                    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
                    return (
                      <div key={item.name} style={adminStyles.barItem}>
                        <div style={adminStyles.barLabel}>{item.name}</div>
                        <div style={adminStyles.barWrapper}>
                          <div
                            style={{
                              ...adminStyles.barFill,
                              width: `${percentage}%`,
                              background: colors[index % colors.length]
                            }}
                          />
                        </div>
                        <div style={adminStyles.barValue}>{item.stock}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {chartType === 'price' && (
              <div style={adminStyles.barChartContainer}>
                <h3 style={{ margin: '0 0 1.5rem 0', color: '#1f2937' }}>Top 10 produits par prix</h3>
                <div style={adminStyles.barChart}>
                  {getPriceData().map((item, index) => {
                    const maxPrice = Math.max(...getPriceData().map(d => d.price));
                    const percentage = (item.price / maxPrice) * 100;
                    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
                    return (
                      <div key={item.name} style={adminStyles.barItem}>
                        <div style={adminStyles.barLabel}>{item.name}</div>
                        <div style={adminStyles.barWrapper}>
                          <div
                            style={{
                              ...adminStyles.barFill,
                              width: `${percentage}%`,
                              background: colors[index % colors.length]
                            }}
                          />
                        </div>
                        <div style={adminStyles.barValue}>{item.price.toLocaleString()} FCFA</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Contrôles de recherche et filtres */}
      <div style={{
        ...adminStyles.controlsContainer,
        ...(isMobile && adminStyles.mobileControlsContainer)
      }}>
        <div style={{
          ...adminStyles.searchAndFilters,
          ...(isMobile && adminStyles.mobileSearchAndFilters)
        }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Search size={20} style={{ 
              position: 'absolute', 
              left: '1rem', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: '#6b7280'
            }} />
            <input
              type="text"
              placeholder="Rechercher des produits..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ ...adminStyles.searchInput, paddingLeft: '3rem' }}
            />
          </div>

          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            style={adminStyles.filterSelect}
          >
            <option value="all">Toutes catégories</option>
            <option value="iphone">iPhone</option>
            <option value="samsung">Samsung</option>
            <option value="accessory">Accessoires</option>
            <option value="other">Autres</option>
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={adminStyles.filterSelect}
          >
            <option value="all">Tous les statuts</option>
            <option value="low-stock">Stock faible</option>
            <option value="out-of-stock">Rupture de stock</option>
            <option value="popular">Populaires</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={adminStyles.filterSelect}
          >
            <option value="name">Trier par nom</option>
            <option value="price">Trier par prix</option>
            <option value="stock">Trier par stock</option>
            <option value="created_at">Trier par date</option>
          </select>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => setViewMode('grid')}
              style={{ 
                ...adminStyles.actionButton, 
                background: viewMode === 'grid' ? '#4f46e5' : '#f3f4f6',
                color: viewMode === 'grid' ? 'white' : '#374151'
              }}
              title="Vue grille"
            >
              <Grid size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{ 
                ...adminStyles.actionButton, 
                background: viewMode === 'list' ? '#4f46e5' : '#f3f4f6',
                color: viewMode === 'list' ? 'white' : '#374151'
              }}
              title="Vue liste"
            >
              <List size={16} />
            </button>
            <button
              onClick={() => setShowDataViz(!showDataViz)}
              style={{ 
                ...adminStyles.actionButton, 
                background: showDataViz ? '#4f46e5' : '#f3f4f6',
                color: showDataViz ? 'white' : '#374151'
              }}
              title="Visualisation de données"
            >
              <BarChart2 size={16} />
            </button>
          </div>

          <button
            onClick={() => setShowStats(!showStats)}
            style={{ ...adminStyles.actionButton, background: '#f3f4f6', color: '#374151' }}
          >
            <BarChart3 size={16} />
          </button>

          <button
            onClick={loadProducts}
            style={{ ...adminStyles.actionButton, background: '#f3f4f6', color: '#374151' }}
          >
            <RefreshCw size={16} />
          </button>

          <button
            onClick={() => setShowForm(true)}
            style={{ ...adminStyles.button, ...adminStyles.primaryButton }}
          >
            <Plus size={16} />
            Ajouter un produit
          </button>
        </div>
      </div>

      {/* Liste des produits */}
      <div style={{
        ...adminStyles.productsContainer,
        ...(isMobile && adminStyles.mobileProductsContainer)
      }}>
        <div style={{
          ...(viewMode === 'grid' ? adminStyles.productsGrid : adminStyles.productsList),
          ...(isMobile && viewMode === 'grid' && (isSmallMobile ? adminStyles.smallMobileProductsGrid : adminStyles.mobileProductsGrid))
        }}>
          {filteredAndSortedProducts.map((product) => (
            <div key={product.id} style={{
              ...(viewMode === 'grid' ? adminStyles.productCard : adminStyles.productCardList),
              ...(isMobile && viewMode === 'list' && adminStyles.mobileProductCardList)
            }}>
              {product.images && product.images.length > 0 ? (
                <img 
                  src={product.images[0].image.startsWith('http') 
                    ? product.images[0].image 
                    : `https://inza-backend.sajholding.org${product.images[0].image}`
                  } 
                  alt={product.name} 
                  style={{
                    ...(viewMode === 'grid' ? adminStyles.productImage : adminStyles.productListImage),
                    ...(isMobile && viewMode === 'list' && adminStyles.mobileProductListImage)
                  }} 
                />
              ) : product.image_url ? (
                <img 
                  src={product.image_url.startsWith('http') 
                    ? product.image_url 
                    : `https://inza-backend.sajholding.org${product.image_url}`
                  } 
                  alt={product.name} 
                  style={{
                    ...(viewMode === 'grid' ? adminStyles.productImage : adminStyles.productListImage),
                    ...(isMobile && viewMode === 'list' && adminStyles.mobileProductListImage)
                  }} 
                />
              ) : (
                <div style={{ 
                  ...(viewMode === 'grid' ? adminStyles.productImage : adminStyles.productListImage),
                  ...(isMobile && viewMode === 'list' && adminStyles.mobileProductListImage),
                  background: '#f3f4f6', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <ImageIcon size={viewMode === 'grid' ? 48 : 32} style={{ color: '#9ca3af' }} />
                </div>
              )}
              
              <div style={{
                ...(viewMode === 'grid' ? {} : adminStyles.productListInfo),
                ...(isMobile && viewMode === 'list' && adminStyles.mobileProductListInfo)
              }}>
                <h3 style={{ margin: viewMode === 'grid' ? '0 0 0.5rem 0' : '0 0 0.25rem 0', color: '#1f2937', fontSize: viewMode === 'grid' ? '1.1rem' : '1rem' }}>
                  {product.name}
                </h3>
                <p style={{ color: '#6b7280', fontSize: viewMode === 'grid' ? '0.875rem' : '0.8rem', margin: '0 0 0.5rem 0' }}>
                  {product.category} • Stock: {product.stock}
                </p>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: viewMode === 'grid' ? '1rem' : '0.5rem' }}>
                  <span style={{ fontSize: viewMode === 'grid' ? '1.5rem' : '1.2rem', fontWeight: 'bold', color: '#1f2937' }}>
                    {product.price.toLocaleString()} FCFA
                  </span>
                  {product.popular && (
                    <span style={{ 
                      background: '#fef3c7', 
                      color: '#d97706', 
                      padding: '0.25rem 0.5rem', 
                      borderRadius: '9999px',
                      fontSize: '0.75rem'
                    }}>
                      Populaire
                    </span>
                  )}
                </div>
              </div>

              <div style={{
                ...(viewMode === 'grid' ? adminStyles.productActions : adminStyles.productListActions),
                ...(isMobile && viewMode === 'list' && adminStyles.mobileProductListActions)
              }}>
                <button 
                  style={{ ...(viewMode === 'grid' ? adminStyles.actionButton : adminStyles.actionButtonSmall), background: '#3b82f6', color: 'white' }}
                  onClick={() => {
                    setSelectedProduct(product);
                    setShowProductDetails(true);
                  }}
                  title="Voir les détails"
                >
                  <Eye size={viewMode === 'grid' ? 16 : 14} />
                </button>
                <button 
                  style={{ ...(viewMode === 'grid' ? adminStyles.actionButton : adminStyles.actionButtonSmall), background: '#f59e0b', color: 'white' }}
                  onClick={() => {
                    // Pré-remplir le formulaire avec les données du produit
                    setFormData({
                      name: product.name,
                      price: product.price,
                      original_price: product.original_price || '',
                      description: product.description,
                      category: product.category,
                      stock: product.stock,
                      popular: product.popular,
                      image: null,
                      storage_options: '',
                      colors: '',
                      slug: product.slug || ''
                    });
                    setShowForm(true);
                  }}
                  title="Modifier"
                >
                  <Edit2 size={viewMode === 'grid' ? 16 : 14} />
                </button>
                <button 
                  style={{ ...(viewMode === 'grid' ? adminStyles.actionButton : adminStyles.actionButtonSmall), background: '#ef4444', color: 'white' }}
                  onClick={() => {
                    if (window.confirm(`Êtes-vous sûr de vouloir supprimer "${product.name}" ?`)) {
                      // TODO: Implémenter la suppression
                      console.log('Supprimer le produit:', product.id);
                    }
                  }}
                  title="Supprimer"
                >
                  <Trash2 size={viewMode === 'grid' ? 16 : 14} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredAndSortedProducts.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'white' }}>
            <Package size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
            <h3>Aucun produit trouvé</h3>
            <p>Essayez de modifier vos filtres ou d'ajouter un nouveau produit.</p>
          </div>
        )}
      </div>

      {/* Modal pour les détails du produit */}
      {showProductDetails && selectedProduct && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2>Détails du produit</h2>
              <button
                onClick={() => setShowProductDetails(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <X size={24} />
              </button>
            </div>
            
            {selectedProduct.images && selectedProduct.images.length > 0 ? (
              <img 
                src={selectedProduct.images[0].image.startsWith('http') 
                  ? selectedProduct.images[0].image 
                  : `https://inza-backend.sajholding.org${selectedProduct.images[0].image}`
                } 
                alt={selectedProduct.name} 
                style={{ 
                  width: '100%', 
                  height: '200px', 
                  objectFit: 'cover', 
                  borderRadius: '8px',
                  marginBottom: '1rem'
                }} 
              />
            ) : selectedProduct.image_url ? (
              <img 
                src={selectedProduct.image_url.startsWith('http') 
                  ? selectedProduct.image_url 
                  : `https://inza-backend.sajholding.org${selectedProduct.image_url}`
                } 
                alt={selectedProduct.name} 
                style={{ 
                  width: '100%', 
                  height: '200px', 
                  objectFit: 'cover', 
                  borderRadius: '8px',
                  marginBottom: '1rem'
                }} 
              />
            ) : null}
            
            <h3>{selectedProduct.name}</h3>
            <p style={{ color: '#6b7280', marginBottom: '1rem' }}>{selectedProduct.description}</p>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <strong>Prix:</strong> {selectedProduct.price.toLocaleString()} FCFA
              </div>
              <div>
                <strong>Stock:</strong> {selectedProduct.stock}
              </div>
              <div>
                <strong>Catégorie:</strong> {selectedProduct.category}
              </div>
              <div>
                <strong>Populaire:</strong> {selectedProduct.popular ? 'Oui' : 'Non'}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              <button
                onClick={() => setShowProductDetails(false)}
                style={{ ...adminStyles.button, ...adminStyles.secondaryButton, flex: 1 }}
              >
                Fermer
              </button>
              <button
                onClick={() => {
                  setShowProductDetails(false);
                  // Pré-remplir le formulaire pour l'édition
                  setFormData({
                    name: selectedProduct.name,
                    price: selectedProduct.price,
                    original_price: selectedProduct.original_price || '',
                    description: selectedProduct.description,
                    category: selectedProduct.category,
                    stock: selectedProduct.stock,
                    popular: selectedProduct.popular,
                    image: null,
                    storage_options: '',
                    colors: '',
                    slug: selectedProduct.slug || ''
                  });
                  setShowForm(true);
                }}
                style={{ ...adminStyles.button, ...adminStyles.primaryButton, flex: 1 }}
              >
                Modifier
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Formulaire d'ajout/modification */}
      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '12px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2>Ajouter un produit</h2>
              <button
                onClick={() => setShowForm(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              // TODO: Implémenter la soumission du formulaire
              console.log('Soumettre:', formData);
              setShowForm(false);
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Nom *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Prix *</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    required
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                  />
                </div>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                />
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Catégorie</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                  >
                    <option value="iphone">iPhone</option>
                    <option value="samsung">Samsung</option>
                    <option value="accessory">Accessoire</option>
                    <option value="other">Autre</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Stock</label>
                  <input
                    type="number"
                    name="stock"
                    value={formData.stock}
                    onChange={(e) => setFormData({...formData, stock: e.target.value})}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      name="popular"
                      checked={formData.popular}
                      onChange={(e) => setFormData({...formData, popular: e.target.checked})}
                    />
                    Produit populaire
                  </label>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  style={{ ...adminStyles.button, ...adminStyles.secondaryButton, flex: 1 }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  style={{ ...adminStyles.button, ...adminStyles.primaryButton, flex: 1 }}
                >
                  Ajouter le produit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductAdminEnhanced;
